import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { SmsHubLayout, StatCard } from "@/components/sms/SmsHubLayout";
import { getSmsOverview, listQueue } from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/sms/analytics")({
  head: () => ({ meta: [{ title: "Analytics · SMS" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

function Page() {
  const { primaryRole } = useAuth();
  const overview = useServerFn(getSmsOverview);
  const queue = useServerFn(listQueue);

  const { data: stats, isLoading } = useQuery({ queryKey: ["sms-overview"], queryFn: () => overview(), enabled: primaryRole === "admin", refetchInterval: 30_000 });
  const { data: qrows } = useQuery({ queryKey: ["sms-queue-all"], queryFn: () => queue({ data: { limit: 500 } }), enabled: primaryRole === "admin" });

  // Bucket queue rows by hour-of-day for "sends by hour"
  const byHour = Array.from({ length: 24 }, (_, h) => ({ hour: `${h}:00`, scheduled: 0 }));
  (qrows ?? []).forEach((r: any) => {
    const h = new Date(r.scheduled_at).getHours();
    byHour[h].scheduled += 1;
  });

  // By Twilio number
  const numberMap = new Map<string, number>();
  (qrows ?? []).forEach((r: any) => {
    const n = r.sms_twilio_numbers?.e164 ?? "unassigned";
    numberMap.set(n, (numberMap.get(n) ?? 0) + 1);
  });
  const byNumber = Array.from(numberMap.entries()).map(([number, count]) => ({ number, count }));

  if (isLoading || !stats) return <SmsHubLayout title="Live Analytics"><div className="py-8 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div></SmsHubLayout>;
  const s = stats as any;

  return (
    <SmsHubLayout title="Live Analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <StatCard label="Sent today" value={s.sent_today} />
        <StatCard label="Failed today" value={s.failed_today} />
        <StatCard label="Delivery rate" value={s.sent_today ? `${Math.round((s.sent_today / (s.sent_today + s.failed_today || 1)) * 100)}%` : "—"} />
        <StatCard label="Replies" value={s.replies} />
        <StatCard label="Opt-outs" value={s.opt_outs} />
        <StatCard label="Remaining quota" value={Math.max(0, 5000 - s.sent_today)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border rounded-md bg-card p-4">
          <h3 className="text-[13px] font-semibold mb-3">Scheduled sends by hour</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byHour}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="hour" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip />
              <Bar dataKey="scheduled" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border rounded-md bg-card p-4">
          <h3 className="text-[13px] font-semibold mb-3">Distribution by Twilio number</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byNumber} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis type="number" fontSize={10} />
              <YAxis type="category" dataKey="number" fontSize={10} width={120} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground mt-4">Charts reflect the current dry-run queue. Once Live Mode is enabled in Phase 2, delivery/reply/click rates will also populate here.</p>
    </SmsHubLayout>
  );
}
